'use client';

import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import {
  CardHeader,
  CardTitle,
  CardContent,
  Card,
} from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Star } from 'lucide-react';
import React from 'react';
import TrendingIcon from '@/../public/static/icons/trending-icon.svg';
import type { Item } from '@/api/use-get-personalize';
import { useNavigation } from '@/components/navigation/navigation-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuth } from '@/providers/auth-provider';

interface PersonalizeItemProps {
  item: Item;
  isCenter: boolean;
  index: number;
  handleSuggestionClick?: (suggestion: string) => void;
  handleItemClick: (item: Item) => void;
}

// Simple Badge component as fallback
const Badge = ({
  children,
  className = '',
  variant = 'default',
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'secondary';
}) => {
  const baseClasses =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold';
  const variantClasses = {
    default: 'bg-blue-500 text-white',
    secondary: 'bg-neutral-20 text-neutral-80',
  };

  return (
    <span className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function PersonalizeItem({
  item,
  isCenter,
  index,
  handleSuggestionClick,
  handleItemClick,
}: PersonalizeItemProps) {
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuth();
  const { setForceHidden } = useNavigation();
  const isMobile = useIsMobile();

  const handleCardClick = (item: Item) => {
    if (item.slug) {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'for_you_card_click',
        button_location: 'for_you_section',
        storeId: item.store_id,
        item_name: item.name,
        item_id: item._id,
        position: index + 1,
        user_id: user?.cunstomerId,
      });
      setForceHidden(false);
      router.push(routes.storeDetail(item.slug));

      if (
        typeof window !== 'undefined' &&
        typeof window.setOpenChange === 'function'
      ) {
        window.setOpenChange(false);
      }
    }
  };

  const handleSameItemClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'for_you_card_same_item_click',
      button_location: 'for_you_section',
      storeId: item.store_id,
      item_name: item.name,
      item_id: item._id,
      position: index + 1,
      user_id: user?.cunstomerId,
    });
    const suggestion = `Các món tương tự món ${item.name} phù hợp với tôi`;
    if (handleSuggestionClick) {
      handleSuggestionClick(suggestion);
    }
    // Handle item click - navigate to store or add to cart
  };
  let rawRating: number | null = null;
  // Ưu tiên average
  if (item.rating?.average && item.rating.average > 0) {
    rawRating = item.rating.average;
  } else if (
    item.rating?.google_average &&
    item.rating.google_average >= 4 &&
    item.rating.total === 0
  ) {
    rawRating = item.rating.google_average;
  }

  const showRating = rawRating !== null; // chỉ show khi có giá trị hợp lệ
  const showSold = item.total_sold > 10;
  let soldDisplay = '';
  if (showSold) {
    soldDisplay =
      item.total_sold > 999
        ? `${(item.total_sold / 1000).toFixed(1)}K`
        : `${item.total_sold}`;
  }

  const isSoloMeal = item.extra_tags.includes('SOLO_MEAL');
  const ratingElement = showRating ? (
    <div className="flex items-center text-[10px]">
      {item.rating?.total === 0 ? (
        <img
          src="/static/images/gg-rating.webp"
          alt="Store"
          className="inline-block mr-1 h-auto w-4 mb-0.5"
        />
      ) : (
        <Star className="size-3 mr-1 fill-yellow-40 text-yellow-40" />
      )}
      <span className="font-bold">{rawRating!.toFixed(1)}</span>
    </div>
  ) : null;
  const soldElement = showSold ? (
    <span className="text-[10px]">
      <img
        src="/static/icons/bag.svg"
        alt="Store"
        className="inline-block size-3 mr-1"
      />
      {soldDisplay} lượt bán
    </span>
  ) : null;

  return (
    <Card
      onClick={() => handleCardClick(item)}
      key={item._id}
      className={cn(
        'shrink-0 w-[200px] md:w-full md:bg-white/30 personalize-item md:border md:border-white/70 overflow-hidden rounded-2xl md:rounded-2xl flex flex-col md:flex-row gap-0 relative transition-all duration-300',
        'cursor-pointer',
        isCenter && isMobile
          ? 'personalize-item-center shadow-md md:shadow-xl'
          : 'shadow-sm md:shadow-md'
      )}
    >
      {item.is_ai_image && (
        <Badge className="z-50 size-7 md:size-8 absolute top-2 md:top-2 right-2 md:right-2 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1 text-[10px] font-bold">
          <Image
            fill
            className="p-1"
            src={`${assetPrefix}/static/icons/white-ai.svg`}
            alt={''}
          />
        </Badge>
      )}
      {isSoloMeal && (
        <div className="absolute top-2 md:top-2 left-2 md:left-2 z-10">
          <Image
            width={71}
            height={21}
            className="ml-1 md:w-20 md:h-6"
            src={`${assetPrefix}/static/images/solo-meal.png`}
            alt={'Solo Meal'}
          />
        </div>
      )}

      <div className="relative w-full md:w-[180px] shrink-0">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            className="object-cover size-full aspect-[4/3] md:aspect-square"
            src={item.image_thumb_url || '/static/images/empty-store-bot.webp'}
          />
        </Avatar>
        <div className="absolute bottom-0 left-0 flex items-end gap-0">
          <Badge
            variant="secondary"
            className="flex items-center w-fit h-5 md:h-6 bg-white rounded-none rounded-tr-2xl md:rounded-tr-2xl"
          >
            <img
              src={`${assetPrefix}/static/icons/clock.svg`}
              alt="ETA"
              className="mr-1 size-3 md:size-3.5"
            />
            <span className="text-[10px] md:text-xs text-center w-full font-semibold pt-1 pr-1">
              Từ {item.eta} phút
            </span>
          </Badge>
          <img
            src={`${assetPrefix}/static/icons/badge-tail.svg`}
            alt="ETA"
            className="size-4 md:size-4 object-contain pointer-events-none -mb-0.5 -ml-0.5"
          />
        </div>
      </div>

      {/* Right section: Details and Actions */}
      <div className="flex-1 flex flex-col justify-between md:content-start px-3 pt-2 md:py-3 pb-3 gap-1 md:gap-1.5">
        <CardHeader className="p-0">
          <CardTitle className="text-xs md:text-sm font-bold text-neutral-80 line-clamp-1 md:line-clamp-2">
            {item.trend_item ? (
              <TrendingIcon className="inline-block mr-1 animate-fire-pulse" />
            ) : null}
            {item.name}
          </CardTitle>
        </CardHeader>

        <div
          className={cn(
            'text-neutral-70 rounded md:rounded-md px-2 py-1.5 md:py-1.5 text-xs gap-0.5 flex flex-col items-start',
            isSoloMeal
              ? 'bg-gradient-to-r from-[#FFEAA8]/70 via-[#FFEFDC]/70 to-[#FDC9B9]/70'
              : 'bg-neutral-10/80'
          )}
        >
          <div
            className={cn(
              'flex text-neutral-50 text-xs md:text-xs h-auto',
              (ratingElement ?? soldElement) ? 'items-center' : 'items-start'
            )}
          >
            <img
              src="/static/icons/store.svg"
              alt="Store"
              className="inline-block size-3 md:size-3.5 mr-1"
            />
            <span
              className={cn(
                'font-bold text-[10px] md:text-[10px]',
                (ratingElement ?? soldElement) ? 'line-clamp-1' : 'line-clamp-2'
              )}
            >
              {item.distance.toFixed(1)} km -{' '}
              <span className="font-medium">{item.page_name}</span>
            </span>
          </div>
          {(ratingElement ?? soldElement) && (
            <div className="flex items-start text-neutral-50 text-xs md:text-sm h-3">
              {ratingElement}
              {ratingElement && soldElement && (
                <span className="mx-2 text-neutral-50">|</span>
              )}
              {soldElement}
            </div>
          )}
        </div>
        <div className="flex items-baseline space-x-2 text-xs">
          {item.discount_price ? (
            <div className="flex gap-3 items-end flex-wrap">
              <span className="font-bold text-primary-40 text-sm md:text-base">
                {item.discount_price.toLocaleString()} đ
              </span>
              <span className="line-through text-neutral-40 font-bold text-xs">
                {item.price.toLocaleString()} đ
              </span>
            </div>
          ) : (
            <span className="font-bold text-primary-40 text-sm md:text-base">
              {item.price.toLocaleString()} đ
            </span>
          )}
        </div>

        <CardContent className="p-0 flex items-center justify-between gap-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSameItemClick();
            }}
            variant="ghost"
            className="w-full min-w-fit font-bold flex gap-0 p-2 md:p-2.5 bg-[#FFF5F5] md:bg-white/40 hover:bg-[#FFE5E5] text-[10px] md:text-xs h-7 md:h-8 transition-colors"
          >
            <img
              className="size-4 object-contain"
              src="/static/icons/ai-star.svg"
              alt="star"
            />
            <span>Món tương tự</span>
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleItemClick(item);
            }}
            className="w-full bg-gradient-to-r from-[#FFA339] to-[#FF7F32] hover:from-[#FF9320] hover:to-[#FF6E1A] text-white font-bold p-2 md:p-2.5 flex gap-0 text-[10px] md:text-xs h-7 md:h-8 transition-all"
          >
            Đặt ngay
          </Button>
        </CardContent>
      </div>
    </Card>
  );
}
